import React, { useState, useEffect } from "react";
import axios from "axios";
import GetCookie from "../../utils/GetCookie";
import ErrorPopup from "../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../utils/popup/SuccessPopup";
import DocumentSelector from "./DocumentSelector";
import authNavigate from "../../globalComponents/AuthNav";
import { useNavigate } from "react-router-dom";

// A dedicated wrapper for the embedded document selector to provide a consistent header.
const EmbeddedDocumentSelector = ({
  fabricTreeData,
  selectedVmDocIds,
  handleVmDocumentSelection,
  selectAllDocuments,
  deselectAllDocuments,
  totalDocumentCount,
}) => {
  if (!fabricTreeData || fabricTreeData.length === 0) {
    return (
      <div className="ev-card-2">
        <div className="text-center">
          <i className="bi bi-hourglass-split ev-reg-p-blue"></i>
          <p className="ev-small-p-blue mt-2">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-100">
      {/* Header for the document selector (border-bottom removed) */}
      <div className="d-flex justify-content-between align-items-center mt-5 mb-2">
        <div>
          <h6 className="ev-reg-p-blue ev-bold-med mb-0">Select Data</h6>
        </div>
        <div>
          <button
            type="button"
            className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small ms-2"
            onClick={selectAllDocuments}
          >
            Select All
          </button>
          <button
            type="button"
            className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small ms-2"
            onClick={deselectAllDocuments}
          >
            Deselect All
          </button>
          <span className="badge bg-primary ms-2 ev-btn-reg">
            {selectedVmDocIds.length} of {totalDocumentCount} selected
          </span>
        </div>
      </div>
      <div className="">
      <div className="ev-card-3 ev-radius-small p-0">
        <DocumentSelector
          isEmbedded={true}
          fabricTreeData={fabricTreeData}
          selectedDocIds={selectedVmDocIds}
          onDocumentSelect={handleVmDocumentSelection}
          sectionName="Value Message Configuration"
          sectionOrderingId="VM"
          agentName="Document Selection"
          agentOrderingId="1"
        />
      </div>
      </div>
    </div>
  );
};

const AddEditVM = ({
  editingVM = null,
  selectedTemplate,
  fabric_id,
  onClose,
  onSuccess,
  Configuration,
  fabricTreeData,
  SubTypes
}) => {
  const isEditing = !!editingVM;

  // Form states
  const [valueMessage, setValueMessage] = useState("");
  const [category, setCategory] = useState("");
  const [selectedSubTypes, setSelectedSubTypes] = useState([]);

  // Document selection states
  const [selectedVmDocIds, setSelectedVmDocIds] = useState([]);

  // Available options
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categorySubtypeMapping, setCategorySubtypeMapping] = useState([]);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");

  // Template selection state
  const [currentTemplateName, setCurrentTemplateName] = useState("");

  // Track if we're in the initial editing setup to prevent subtypes from being cleared
  const [isInitialSetup, setIsInitialSetup] = useState(false);

  const navigate = useNavigate();

  // HTTP headers
  const headers = {
    Authorization: GetCookie("access_token"),
  };

  const parseCategorySubtypeMapping = (categoryStr, subtypesData) => {
    if (!categoryStr) return { categories: [], mapping: [] };

    let subtypesArray = [];
    if (Array.isArray(subtypesData)) {
      subtypesArray = subtypesData;
    } else if (typeof subtypesData === "string") {
      subtypesArray = subtypesData.split(",").map((s) => s.trim()).filter(Boolean);
    }

    const categoryMappings = categoryStr
      .split(",")
      .map((mapping) => {
        const [name, countStr] = mapping.split(":").map((s) => s.trim());
        return { name, count: parseInt(countStr) || 0 };
      })
      .filter((mapping) => mapping.name);

    const categories = categoryMappings.map((m) => m.name);

    let subtypeIndex = 0;
    const detailedMapping = categoryMappings.map((mapping) => {
      const categorySubtypes = subtypesArray.slice(subtypeIndex, subtypeIndex + mapping.count);
      subtypeIndex += mapping.count;

      return { category: mapping.name, subtypes: categorySubtypes };
    });

    return { categories, mapping: detailedMapping };
  };

  useEffect(() => {
    if (Configuration) {
      let parsedData = { categories: [], mapping: [] };

      if (Configuration.categories && Array.isArray(Configuration.categories)) {
        parsedData = {
          categories: Configuration.categories.map((cat) => cat.name),
          mapping: Configuration.categories.map((cat) => ({
            category: cat.name,
            subtypes: cat.subtypes || [],
          })),
        };
      } else if (Configuration.category && Configuration.subcategory) {
        parsedData = parseCategorySubtypeMapping(Configuration.category, Configuration.subcategory);
      } else if (Configuration.type_name && Configuration.sub_types) {
        parsedData = parseCategorySubtypeMapping(Configuration.type_name, Configuration.sub_types);
      } else if (Configuration.type_name) {
        const categories = Configuration.type_name.split(",").map((cat) => cat.trim()).filter((cat) => cat.length > 0);
        parsedData = { categories, mapping: [] };
      }

      setAvailableCategories(parsedData.categories);
      setCategorySubtypeMapping(parsedData.mapping);
    }
  }, [Configuration]);

  const getSubtypesForCategory = (selectedCategory) => {
    if (!selectedCategory || categorySubtypeMapping.length === 0) {
      return [];
    }
    const categoryMapping = categorySubtypeMapping.find(
      (mapping) => mapping.category === selectedCategory
    );
    return categoryMapping ? categoryMapping.subtypes : [];
  };

  useEffect(() => {
    if (isEditing && editingVM) {
      setIsInitialSetup(true); // Mark as initial setup
      
      setValueMessage(editingVM.value_message || "");
      
      // Handle category - prioritize type_name
      const categoryValue = editingVM.type_name || editingVM.type || editingVM.category || "";
      setCategory(categoryValue);

      // Handle subtypes
      let subTypesToSet = [];
      if (editingVM.sub_types) {
        subTypesToSet = Array.isArray(editingVM.sub_types) 
          ? editingVM.sub_types 
          : String(editingVM.sub_types).split(",").map((s) => s.trim()).filter(Boolean);
      } else if (editingVM.sub_type) {
        subTypesToSet = Array.isArray(editingVM.sub_type) 
          ? editingVM.sub_type 
          : String(editingVM.sub_type).split(",").map((s) => s.trim()).filter(Boolean);
      }
      setSelectedSubTypes(subTypesToSet);
      
      setCurrentTemplateName(editingVM.template_name || selectedTemplate?.name || "");

      if (editingVM.documents && editingVM.documents.length > 0) {
        setSelectedVmDocIds(editingVM.documents.map((doc) => doc.document_id || doc.id));
      } else if (editingVM.doc_ids && Array.isArray(editingVM.doc_ids)) {
        setSelectedVmDocIds(editingVM.doc_ids);
      }
      
      // Small delay to ensure initial setup is complete
      setTimeout(() => {
        setIsInitialSetup(false);
      }, 100);
    } else if (!isEditing) {
      setValueMessage("");
      setCategory("");
      setSelectedSubTypes([]);
      setCurrentTemplateName(selectedTemplate?.name || "");
      setSelectedVmDocIds([]);
      setIsInitialSetup(false);
    }
  }, [editingVM, isEditing, selectedTemplate]);

  // Only clear subtypes when category changes AND not during initial setup
  useEffect(() => {
    if (!isEditing && !isInitialSetup) {
      setSelectedSubTypes([]);
    }
  }, [category, isEditing, isInitialSetup]);

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
  };

  const handleVmDocumentSelection = (docIds) => {
    setSelectedVmDocIds(docIds);
  };

  const handleSubTypeChange = (e) => {
    const value = e.target.value;
    if (value && !selectedSubTypes.includes(value)) {
      const currentCategorySubtypes = getSubtypesForCategory(category);
      if (currentCategorySubtypes.includes(value)) {
        setSelectedSubTypes([...selectedSubTypes, value]);
      }
    }
  };

  const removeSubType = (subTypeToRemove) => {
    setSelectedSubTypes(selectedSubTypes.filter((st) => st !== subTypeToRemove));
  };

  const getSelectedDocumentNames = () => {
    if (!fabricTreeData || fabricTreeData.length === 0 || selectedVmDocIds.length === 0) return [];
    const fabric = fabricTreeData[0];
    const documentNames = [];
    fabric.threads?.forEach((thread) => {
      thread.documents?.forEach((doc) => {
        if (selectedVmDocIds.includes(doc.id)) {
          documentNames.push(doc.document_name);
        }
      });
    });
    return documentNames;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valueMessage.trim()) {
      setPopupErrorMessage("Value message is required");
      setIsErrorPopupVisible(true);
      return;
    }
    if (!isEditing && !category.trim() && availableCategories.length > 0) {
      setPopupErrorMessage("Category is required");
      setIsErrorPopupVisible(true);
      return;
    }
    if (category.trim()) {
      const currentCategorySubtypes = getSubtypesForCategory(category);
      const invalidSubtypes = selectedSubTypes.filter(
        (subtype) => !currentCategorySubtypes.includes(subtype)
      );
      if (invalidSubtypes.length > 0 && currentCategorySubtypes.length > 0) {
        setPopupErrorMessage(`Invalid subtypes selected for category "${category}": ${invalidSubtypes.join(", ")}`);
        setIsErrorPopupVisible(true);
        return;
      }
    }
    if (!selectedTemplate) {
      setPopupErrorMessage("Please select a template");
      setIsErrorPopupVisible(true);
      return;
    }
    if ((!selectedVmDocIds || selectedVmDocIds.length === 0) && !isEditing) {
      setPopupErrorMessage("Please select at least one document");
      setIsErrorPopupVisible(true);
      return;
    }

    setIsProcessingPopupVisible(true);
    setProcessingMessage(isEditing ? "Updating Value Message" : "Creating Value Message");

    try {
      const requestBody = isEditing
        ? {
          template_id: selectedTemplate,
          fabric_id: fabric_id,
          vm_id: editingVM.id,
          type: category.trim() || null,
          sub_type: selectedSubTypes.length > 0 ? selectedSubTypes : null,
          value_message: valueMessage.trim(),
          doc_ids: selectedVmDocIds.map((id) => String(id)),
        }
        : {
          template_id: selectedTemplate,
          fabric_id: fabric_id,
          add_vm_chunk: [
            {
              type: category.trim(),
              sub_type: selectedSubTypes,
              value_message: valueMessage.trim(),
              doc_ids: selectedVmDocIds,
            },
          ],
        };

      const url = isEditing ? "/pceapi/genbi/value-message/update-value-message" : "/pceapi/genbi/value-message/add-value-message";
      const response = await axios.post(url, requestBody, { headers });

      if (response.data.status === "NotAuthorized") {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      if (response.data.status === "failed") {
        throw new Error(response.data.message || `Failed to ${isEditing ? "update" : "create"} value message`);
      }

      setIsProcessingPopupVisible(false);
      onSuccess(`Value message ${isEditing ? "updated" : "created"} successfully`);
    } catch (error) {
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(error.message);
      setIsErrorPopupVisible(true);
    }
  };

  const selectedDocNames = getSelectedDocumentNames();
  const currentCategorySubtypes = getSubtypesForCategory(category);
  const totalDocumentCount = fabricTreeData?.[0]?.threads?.reduce((count, thread) => count + (thread.documents?.length || 0), 0) || 0;

  const selectAllDocuments = () => {
    const allDocIds = fabricTreeData?.[0]?.threads?.flatMap((thread) => thread.documents?.map((doc) => doc.id) || []) || [];
    setSelectedVmDocIds(allDocIds);
  };

  const deselectAllDocuments = () => {
    setSelectedVmDocIds([]);
  };

  return (
    <>
      <div className="d-flex">
        {/* Left Panel - Form */}
          <div className="pe-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h6 className="ev-med-p-blue mb-1 mt-2 ms-1">
                  {isEditing ? "Update Value Message" : "Create Value Message"}
                </h6>
                {currentTemplateName && (
                  <span className="badge bg-primary">{currentTemplateName}</span>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label ev-small-p-dark">
                    Value Message <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="formField3"
                    rows="4"
                    placeholder="Enter your value message here..."
                    value={valueMessage}
                    onChange={(e) => setValueMessage(e.target.value)}
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label ev-small-p-dark ">
                    Category {availableCategories.length > 0 && !isEditing && <span className="text-danger">*</span>}
                    {isEditing && availableCategories.length === 0 && <i className="ev-small-p-blue"> (Auto-discovered)</i>}
                  </label>
                  {isEditing && availableCategories.length === 0 && category ? (
                    <div className="formField3 d-flex align-items-center" style={{ background: '#f8f9fa', cursor: 'default' }}>
                      <span className="ev-small-p-dark">{category}</span>
                    </div>
                  ) : (
                    <select
                      className="formField3 d-flex justify-content-between align-items-center"
                      value={category}
                      onChange={handleCategoryChange}
                      required={!isEditing && availableCategories.length > 0}
                    >
                      <option value="">{availableCategories.length > 0 ? "Select Category" : "No categories configured"}</option>
                      {availableCategories.map((cat, index) => (
                        <option key={index} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label ev-small-p-dark">
                    Sub Types <i className="ev-small-p-blue">(Optional)</i>
                  </label>
                  <select
                    className="formField3 d-flex justify-content-between align-items-center"
                    value=""
                    onChange={handleSubTypeChange}
                    disabled={!category}
                  >
                    <option value="">
                      {!category ? "Select category first" : "Add Sub Type"}
                    </option>
                    {currentCategorySubtypes.map((subType, index) => (
                      <option
                        key={index}
                        value={subType}
                        disabled={selectedSubTypes.includes(subType)}
                      >
                        {subType}
                      </option>
                    ))}
                  </select>
                  {category && currentCategorySubtypes.length === 0 && (
                    <div className="mt-2 mb-3 text-end">
                      <small className="ev-alert-card-warning ev-btn-xsmall">
                        No subtypes configured for category "{category}"
                      </small>
                    </div>
                  )}
                  {selectedSubTypes.length > 0 && (
                    <div className="mt-2">
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {selectedSubTypes.map((subType, index) => (
                          <span key={index} className="ev-message-card1 ev-btn-small ev-fs-11">
                            {subType}
                            <button
                              type="button"
                              className="ev-btn-text-only-fill ev-btn-small p-0 ms-2"
                              onClick={() => removeSubType(subType)}
                              aria-label="Remove"
                            ><i className ="bi bi-x-circle"></i></button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-12 mt-1 mb-3">
                  {!selectedDocNames.length > 0 ? (
                    <div className="ev-alert-card-warning mt-2 mb-2">
                  <p className="mb-0">
                    <i className="bi bi-info-circle me-1"></i>
                    &nbsp;
                    Please Select atleast one document.
                  </p>
                </div>
                  ) : (
                    <div>{/* Placeholder */}</div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-0 p-0 mt-4">
                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="ev-btn-blue-outline-round ev-bold-reg"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ev-btn-blue-fill-round ev-bold-reg" 
                  >
                    <i className={`bi ${isEditing ? "bi-check-circle" : "bi-plus-circle"} me-1`}></i>
                    {isEditing ? "Update Value Message" : "Create Value Message"}
                  </button>
                </div>
              </div>
            </form>
          </div>

        {/* Right Panel - Document Selector */}
        <div className ="col-5">
          <EmbeddedDocumentSelector
            fabricTreeData={fabricTreeData}
            selectedVmDocIds={selectedVmDocIds}
            handleVmDocumentSelection={handleVmDocumentSelection}
            selectAllDocuments={selectAllDocuments}
            deselectAllDocuments={deselectAllDocuments}
            totalDocumentCount={totalDocumentCount}
          />
        </div>
      </div>

      {isProcessingPopupVisible && (<ProcessingPopup message={processingMessage} />)}
      {isErrorPopupVisible && (<ErrorPopup onClose={() => setIsErrorPopupVisible(false)} message={popupErrorMessage} />)}
    </>
  );
};

export default AddEditVM;